import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from '../../../../shared/store';
import * as api from './api';
import {
    CareTransitionsState,
    CcdaFetchOverrides,
    CcdaFetchRunDto,
    CcdaFetchRunFilter,
    CcdaInboxFilter,
    CcdaReceiveStatus,
    CcdaReconcileSessionDto,
    CcdaTransmissionAuditFilter,
    CcdaTransmitResultDto,
    CcdaTransmitSmtpOverride,
    CcdaUnmatchedReason,
    initialCareTransitionsState,
    PatchDecisionBody,
    ReconcileListDto,
    ReconcileListType,
} from './models';

export const fetchReceived = createAsyncThunk('careTransitions/fetchReceived', async (arg: { patientId?: number; status?: CcdaReceiveStatus }) =>
    api.listReceived(arg.patientId, arg.status)
);

// Global inbound admin queue (Admin ▸ System ▸ C-CDA Exchange, Inbound Queue tab): server-side paged + filtered
// across all patients.
export const fetchInbox = createAsyncThunk('careTransitions/fetchInbox', async (arg: { filter: CcdaInboxFilter; page: { current: number; pageSize: number } }) =>
    api.fetchInboxPage(arg.filter, arg.page)
);

// Per-patient received-document audit (SCRUM-921 AC6 / §170.315(d)(2)/(d)(3)).
export const fetchAudit = createAsyncThunk('careTransitions/fetchAudit', async (arg: { patientId: number }) => api.fetchReceiveAudit(arg.patientId));

// Org-wide received-document audit report (Admin ▸ System): admin-only, server-paged.
export const fetchAuditPage = createAsyncThunk('careTransitions/fetchAuditPage', async (arg: { page: { current: number; pageSize: number } }) =>
    api.fetchReceiveAuditPage(arg.page)
);

// SCRUM-927 outbound Direct transmission. A fulfilled thunk does NOT mean the document was sent — a Failed or
// (where no live Direct transport is enabled) Suppressed attempt resolves too — read `outcome` on the payload.
//
// The optional smtpUserName/smtpPassword are an operator-typed per-send override (b.1 ETT edge testing).
// They ride the thunk ARG and nothing else: no reducer below copies them into state, so they exist only for
// the life of the in-flight request. Do not add them to CareTransitionsState — a credential in the store is
// a credential in the devtools, in a state snapshot, and in any future persistence.
/**
 * What survives a rejected transmit. RTK's default `miniSerializeError` keeps only name/message/stack/code,
 * which drops exactly the fields the send modal classifies on — the ProblemDetails `errorCode` and the HTTP
 * status — leaving it with a generic message and nothing to branch on.
 *
 * Widened through `serializeError` rather than by switching to `rejectWithValue`: that would replace
 * `action.error` with `{ message: 'Rejected' }`, and `unwrap()` would stop throwing something Error-shaped.
 * No credential appears here — the override rides `meta.arg` and is never copied onto the error.
 */
type ApiSerializedError = {
    name?: string;
    message?: string;
    status?: number;
    errorCode?: string;
    detail?: string;
    ccdaTransmissionId?: number;
    clientReference?: string;
};

// RTK's default serializer keeps only name/message/stack/code, which drops the fields the treated-error
// screens branch on: errorCode (a fixed server code), detail (4xx text this codebase wrote at the throw site),
// and the attempt's identifiers a transport failure carries. Stack deliberately omitted: it is never rendered,
// and a rejected action is kept in the devtools.
const serializeApiError = (err: unknown): ApiSerializedError => ({
    name: (err as Error | null)?.name,
    message: (err as Error | null)?.message,
    status: (err as { status?: number } | null)?.status,
    errorCode: (err as { errorCode?: string } | null)?.errorCode,
    detail: (err as { detail?: string } | null)?.detail,
    ccdaTransmissionId: (err as { ccdaTransmissionId?: number } | null)?.ccdaTransmissionId,
    clientReference: (err as { clientReference?: string } | null)?.clientReference,
});

export const transmitDocument = createAsyncThunk<
    CcdaTransmitResultDto,
    { ccdaDocumentId: number; recipientDirectAddress: string } & CcdaTransmitSmtpOverride,
    { serializedErrorType: ApiSerializedError }
>(
    'careTransitions/transmitDocument',
    async (arg) => api.transmitCcda(arg.ccdaDocumentId, arg.recipientDirectAddress, { smtpUserName: arg.smtpUserName, smtpPassword: arg.smtpPassword }),
    { serializeError: serializeApiError }
);

// Per-patient outbound transmission audit (SCRUM-927 / §170.315(d)(2)/(d)(3)).
export const fetchTransmissionAudit = createAsyncThunk('careTransitions/fetchTransmissionAudit', async (arg: { patientId: number }) =>
    api.fetchPatientTransmissionAudit(arg.patientId)
);

// Org-wide outbound transmission audit report (Admin ▸ System): admin-only, server-paged + filtered.
export const fetchTransmissionAuditPage = createAsyncThunk(
    'careTransitions/fetchTransmissionAuditPage',
    async (arg: { filter: CcdaTransmissionAuditFilter; page: { current: number; pageSize: number } }) => api.fetchTransmissionAuditPage(arg.filter, arg.page)
);

// --- SCRUM-922 inbound Direct fetch over IMAP (§170.315(b)(1) receive) -----------------------------------

// Reads the mailbox once. A FULFILLED action is NOT a successful receive: a suppressed inbox or a missing
// mailbox resolves with status 'Failed' and a failureReason. The screen reads the run, not the promise.
export const triggerCcdaFetch = createAsyncThunk<CcdaFetchRunDto, CcdaFetchOverrides | undefined, { serializedErrorType: ApiSerializedError }>(
    'careTransitions/triggerCcdaFetch',
    async (overrides) => api.triggerCcdaFetch(overrides),
    { serializeError: serializeApiError }
);

export const fetchCcdaFetchRuns = createAsyncThunk('careTransitions/fetchCcdaFetchRuns', async (arg: { filter: CcdaFetchRunFilter; page: { current: number; pageSize: number } }) =>
    api.fetchCcdaFetchRuns(arg.filter, arg.page)
);

export const fetchCcdaFetchRunDetail = createAsyncThunk('careTransitions/fetchCcdaFetchRunDetail', async (runId: number) => api.fetchCcdaFetchRunDetail(runId));

// Not a thunk over Redux state: the bytes are PHI and must never enter the store, so the download runs as a
// side effect and only its failure is reported.
export const downloadCcdaFetchPackage = createAsyncThunk('careTransitions/downloadCcdaFetchPackage', async (arg: { runId: number; messageId: number }) =>
    api.downloadCcdaFetchPackage(arg.runId, arg.messageId)
);

export const fetchSession = createAsyncThunk('careTransitions/fetchSession', async (stagingId: number) => api.getSession(stagingId));

// SCRUM-1336 — Dismiss a received document (mark-unmatched) with a required audit reason. Parks it as Rejected,
// reversible via a later resolve. rowVersion is optimistic concurrency: omit for last-write-wins.
export const markUnmatched = createAsyncThunk('careTransitions/markUnmatched', async (arg: { stagingId: number; reason: CcdaUnmatchedReason; rowVersion?: string | null }) =>
    api.markUnmatched(arg.stagingId, arg.reason, arg.rowVersion ?? null)
);

export const fetchList = createAsyncThunk('careTransitions/fetchList', async (arg: { stagingId: number; listType: ReconcileListType }) => api.getList(arg.stagingId, arg.listType));

// A stale write surfaces as a 409; api.ts normalizes every failure to an Error carrying `.status`.
const isConcurrencyConflict = (err: unknown): boolean => (err as { status?: number } | null)?.status === 409;

// A 409 is always reload-and-retry, never a hard failure — another clinician may have edited an item or
// committed the document first. We refresh from the backend and show this notice.
const RECONCILE_CONFLICT_MESSAGE = 'This reconciliation was just updated elsewhere. It has been refreshed with the latest — review and try again.';

// Persist a decision, then re-read the list + session so `reviewed` and the stat counts come
// straight from the backend (a decision change un-reviews the list — never inferred locally).
// On a 409 (another clinician edited the item first) we still refresh so the clinician sees the
// current decision + a fresh rowVersion, and flag `conflict` so the reducer shows a friendly notice.
export const patchDecision = createAsyncThunk(
    'careTransitions/patchDecision',
    async (arg: { stagingId: number; listType: ReconcileListType; itemId: number; body: PatchDecisionBody }) => {
        let conflict = false;
        try {
            await api.patchDecision(arg.stagingId, arg.itemId, arg.body);
        } catch (err) {
            if (!isConcurrencyConflict(err)) throw err;
            conflict = true;
        }
        const [list, session] = await Promise.all([api.getList(arg.stagingId, arg.listType), api.getSession(arg.stagingId)]);
        return { list, session, conflict };
    }
);

// Per-list confirm is attestation only (sets `reviewed`; writes NOTHING — the chart write happens on the
// document-level commit, SCRUM-792 v2). Repeatable, and the list stays editable until commit. Re-read list +
// session so `reviewed`/counts come straight from the backend.
export const confirmList = createAsyncThunk('careTransitions/confirmList', async (arg: { stagingId: number; listType: ReconcileListType }) => {
    await api.confirmList(arg.stagingId, arg.listType);
    const [list, session] = await Promise.all([api.getList(arg.stagingId, arg.listType), api.getSession(arg.stagingId)]);
    return { list, session };
});

// Document-level commit (SCRUM-792): incorporate every confirmed list's accepted items into the chart in one
// atomic write, then re-read the session (now `Reconciled`). A 409 (not all lists confirmed / concurrent commit
// / already reconciled) is reload-and-retry — refresh the session and flag `conflict`; other statuses (e.g.
// un-incorporable coding) reject and surface verbatim.
export const commitReconciliation = createAsyncThunk('careTransitions/commit', async (arg: { stagingId: number; tin?: string }) => {
    let conflict = false;
    try {
        await api.commit(arg.stagingId, arg.tin);
    } catch (err) {
        if (!isConcurrencyConflict(err)) throw err;
        conflict = true;
    }
    const session = await api.getSession(arg.stagingId);
    // On a 409 the cached per-list detail may be stale (a list was un-reviewed elsewhere, or the doc was
    // committed concurrently), so re-read every list too — a session-only refresh would leave the panels
    // showing "✓ Reviewed" with no Confirm control. On success we navigate away, so no list refresh is needed.
    const lists = conflict ? await Promise.all((session.lists ?? []).map((l) => api.getList(arg.stagingId, l.listType))) : [];
    return { session, conflict, lists };
});

const applyListAndSession = (state: CareTransitionsState, payload: { list: ReconcileListDto; session: CcdaReconcileSessionDto }) => {
    state.lists[payload.list.listType] = payload.list;
    state.session = payload.session;
};

const slice = createSlice({
    name: 'careTransitions',
    initialState: initialCareTransitionsState,
    reducers: {
        clearReconcileSession: (state: CareTransitionsState) => {
            state.session = null;
            state.lists = {};
            state.listLoading = {};
            // Clear the worklist too — otherwise a prior patient's document demographics (name/DOB) linger
            // in `received` across a patient switch, especially if the next fetch is slow or fails.
            state.received = [];
            state.receivedLoading = false;
            state.audit = [];
            state.auditLoading = false;
            state.auditPage = [];
            state.auditPageLoading = false;
            state.auditPagePagination = { current: 1, pageSize: 10, total: 0 };
            state.inbox = [];
            state.inboxLoading = false;
            state.inboxPagination = { current: 1, pageSize: 10, total: 0 };
            state.inboxFilter = {};
            state.errorMessage = null;
        },
        // Drop cached lists when moving to a different document so panels don't flash the previous doc's items.
        clearReconcileLists: (state: CareTransitionsState) => {
            state.lists = {};
            state.listLoading = {};
        },
        setCareTransitionsError: (state: CareTransitionsState, action: PayloadAction<string | null>) => {
            state.errorMessage = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchReceived.pending, (state, action) => {
                state.receivedLoading = true;
                state.errorMessage = null;
                // Track the latest request so out-of-order responses (rapid filter changes) can't overwrite it.
                state.receivedRequestId = action.meta.requestId;
            })
            .addCase(fetchReceived.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.receivedRequestId) return; // stale response — a newer fetch is in flight
                state.receivedLoading = false;
                state.received = action.payload;
            })
            .addCase(fetchReceived.rejected, (state, action) => {
                if (action.meta.requestId !== state.receivedRequestId) return;
                state.receivedLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load received documents.';
            })
            .addCase(fetchAudit.pending, (state) => {
                state.auditLoading = true;
                state.errorMessage = null;
            })
            .addCase(fetchAudit.fulfilled, (state, action) => {
                state.auditLoading = false;
                state.audit = action.payload;
            })
            .addCase(fetchAudit.rejected, (state, action) => {
                state.auditLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the audit log.';
            })
            .addCase(fetchInbox.pending, (state, action) => {
                state.inboxLoading = true;
                state.errorMessage = null;
                // Track the latest request so out-of-order responses (rapid filter/page changes) can't overwrite it.
                state.inboxRequestId = action.meta.requestId;
                state.inboxFilter = action.meta.arg.filter;
            })
            .addCase(fetchInbox.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.inboxRequestId) return; // stale response — a newer fetch is in flight
                state.inboxLoading = false;
                state.inbox = action.payload.items;
                state.inboxPagination = action.payload.pagination;
            })
            .addCase(fetchInbox.rejected, (state, action) => {
                if (action.meta.requestId !== state.inboxRequestId) return;
                state.inboxLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the inbound queue.';
            })
            .addCase(fetchSession.pending, (state) => {
                state.sessionLoading = true;
                state.errorMessage = null;
            })
            .addCase(fetchSession.fulfilled, (state, action) => {
                state.sessionLoading = false;
                state.session = action.payload;
            })
            .addCase(fetchSession.rejected, (state, action) => {
                state.sessionLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load reconciliation session.';
            })
            .addCase(markUnmatched.fulfilled, (state, action) => {
                // Reflect Rejected in the cached worklist + session so the surfaces update without a refetch.
                const row = state.received.find((d) => d.stagingId === action.payload.stagingId);
                if (row) {
                    row.status = action.payload.status;
                    // The server clears the match stamp on a dismiss; without this the row keeps showing who matched
                    // it until the tab remounts. providerTin stays, as it does server-side.
                    row.matchedByName = null;
                    row.matchedDate = null;
                }
                if (state.session?.stagingId === action.payload.stagingId) state.session.status = action.payload.status;
            })
            // No markUnmatched.rejected case: the dismissing component surfaces the failure inline (its own
            // dismissError), so setting the feature-wide errorMessage here would show the same failure twice.
            .addCase(fetchAuditPage.pending, (state, action) => {
                state.auditPageLoading = true;
                state.errorMessage = null;
                // Track the latest request so out-of-order responses (rapid paging) can't overwrite it.
                state.auditPageRequestId = action.meta.requestId;
            })
            .addCase(fetchAuditPage.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.auditPageRequestId) return; // stale response
                state.auditPageLoading = false;
                state.auditPage = action.payload.items;
                state.auditPagePagination = action.payload.pagination;
            })
            .addCase(fetchAuditPage.rejected, (state, action) => {
                if (action.meta.requestId !== state.auditPageRequestId) return;
                state.auditPageLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the audit log.';
            })
            .addCase(transmitDocument.pending, (state, action) => {
                state.transmittingDocumentId = action.meta.arg.ccdaDocumentId;
                state.errorMessage = null;
            })
            .addCase(transmitDocument.fulfilled, (state) => {
                state.transmittingDocumentId = null;
            })
            // No errorMessage here, for the reason already recorded on markUnmatched above: the send modal
            // renders this failure inline in its own Alert, so setting the feature-wide message reports one
            // failure twice — and the second copy lands on the tab BEHIND the modal and on the admin C-CDA
            // Exchange screen, where it outlives the modal that explained it.
            .addCase(transmitDocument.rejected, (state) => {
                state.transmittingDocumentId = null;
            })
            .addCase(fetchTransmissionAudit.pending, (state) => {
                state.transmissionAuditLoading = true;
                state.errorMessage = null;
            })
            .addCase(fetchTransmissionAudit.fulfilled, (state, action) => {
                state.transmissionAuditLoading = false;
                state.transmissionAudit = action.payload;
            })
            .addCase(fetchTransmissionAudit.rejected, (state, action) => {
                state.transmissionAuditLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the transmission audit log.';
            })
            .addCase(fetchTransmissionAuditPage.pending, (state, action) => {
                state.transmissionAuditPageLoading = true;
                state.errorMessage = null;
                // Track the latest request so out-of-order responses (rapid paging/filtering) can't overwrite it.
                state.transmissionAuditPageRequestId = action.meta.requestId;
            })
            .addCase(fetchTransmissionAuditPage.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.transmissionAuditPageRequestId) return; // stale response
                state.transmissionAuditPageLoading = false;
                state.transmissionAuditPage = action.payload.items;
                state.transmissionAuditPagePagination = action.payload.pagination;
            })
            .addCase(fetchTransmissionAuditPage.rejected, (state, action) => {
                if (action.meta.requestId !== state.transmissionAuditPageRequestId) return;
                state.transmissionAuditPageLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the transmission audit log.';
            })
            .addCase(triggerCcdaFetch.pending, (state) => {
                state.fetching = true;
                state.errorMessage = null;
            })
            .addCase(triggerCcdaFetch.fulfilled, (state, action) => {
                state.fetching = false;
                // Stored, not interpreted: a run that read nothing is still the answer to "what happened",
                // and the screen decides how to render Failed vs Completed.
                state.lastFetchRun = action.payload;
            })
            .addCase(triggerCcdaFetch.rejected, (state, action) => {
                state.fetching = false;
                state.lastFetchRun = null;
                // A 4xx detail is text this codebase wrote at the throw site (a fetch already running) and says
                // more than the generic message; a 5xx never carries one, because toApiError drops it.
                state.errorMessage = action.error.detail ?? action.error.message ?? 'Failed to read the Direct mailbox.';
            })
            .addCase(fetchCcdaFetchRuns.pending, (state, action) => {
                state.fetchRunsLoading = true;
                state.errorMessage = null;
                // Track the latest request so out-of-order responses (rapid paging/filtering) can't overwrite it.
                state.fetchRunsRequestId = action.meta.requestId;
            })
            .addCase(fetchCcdaFetchRuns.fulfilled, (state, action) => {
                if (action.meta.requestId !== state.fetchRunsRequestId) return; // stale response
                state.fetchRunsLoading = false;
                state.fetchRuns = action.payload.items;
                state.fetchRunsPagination = action.payload.pagination;
            })
            .addCase(fetchCcdaFetchRuns.rejected, (state, action) => {
                if (action.meta.requestId !== state.fetchRunsRequestId) return;
                state.fetchRunsLoading = false;
                state.errorMessage = action.error.message ?? 'Failed to load the inbound fetch history.';
            })
            .addCase(fetchCcdaFetchRunDetail.pending, (state, action) => {
                state.fetchRunDetailLoadingId = action.meta.arg;
                state.errorMessage = null;
            })
            .addCase(fetchCcdaFetchRunDetail.fulfilled, (state, action) => {
                state.fetchRunDetailLoadingId = null;
                state.fetchRunDetails[action.payload.run.id] = action.payload.messages;
            })
            .addCase(fetchCcdaFetchRunDetail.rejected, (state, action) => {
                state.fetchRunDetailLoadingId = null;
                state.errorMessage = action.error.message ?? 'Failed to load the inbound fetch history.';
            })
            .addCase(downloadCcdaFetchPackage.rejected, (state, action) => {
                state.errorMessage = action.error.message ?? 'Failed to download the XDM package.';
            })
            .addCase(fetchList.pending, (state, action) => {
                state.listLoading[action.meta.arg.listType] = true;
                state.errorMessage = null;
            })
            .addCase(fetchList.fulfilled, (state, action) => {
                state.listLoading[action.payload.listType] = false;
                state.lists[action.payload.listType] = action.payload;
            })
            .addCase(fetchList.rejected, (state, action) => {
                state.listLoading[action.meta.arg.listType] = false;
                state.errorMessage = action.error.message ?? 'Failed to load reconciliation list.';
            })
            .addCase(patchDecision.pending, (state, action) => {
                state.savingItemId = action.meta.arg.itemId;
                state.errorMessage = null;
            })
            .addCase(patchDecision.fulfilled, (state, action) => {
                state.savingItemId = null;
                applyListAndSession(state, action.payload);
                state.errorMessage = action.payload.conflict ? RECONCILE_CONFLICT_MESSAGE : null;
            })
            .addCase(patchDecision.rejected, (state, action) => {
                state.savingItemId = null;
                state.errorMessage = action.error.message ?? 'Failed to save decision.';
            })
            .addCase(confirmList.pending, (state, action) => {
                state.confirmingListType = action.meta.arg.listType;
                state.errorMessage = null;
            })
            .addCase(confirmList.fulfilled, (state, action) => {
                state.confirmingListType = null;
                applyListAndSession(state, action.payload);
            })
            .addCase(confirmList.rejected, (state, action) => {
                state.confirmingListType = null;
                state.errorMessage = action.error.message ?? 'Failed to confirm list.';
            })
            .addCase(commitReconciliation.pending, (state) => {
                state.committing = true;
                state.errorMessage = null;
            })
            .addCase(commitReconciliation.fulfilled, (state, action) => {
                state.committing = false;
                state.session = action.payload.session;
                // Apply any refreshed lists (populated only on the 409 reload path) so the panels reflect reality.
                action.payload.lists.forEach((list) => {
                    state.lists[list.listType] = list;
                });
                // Only nudge "reload and retry" when there's still something to do — if the doc came back
                // Reconciled (e.g. a concurrent commit won the race), it's complete, so show no error.
                state.errorMessage = action.payload.conflict && action.payload.session.status !== 'Reconciled' ? RECONCILE_CONFLICT_MESSAGE : null;
                // Reflect the new status in the cached worklist so the Received tab shows "View" (not "View & Reconcile").
                const row = state.received.find((d) => d.stagingId === action.payload.session.stagingId);
                if (row) row.status = action.payload.session.status;
            })
            .addCase(commitReconciliation.rejected, (state, action) => {
                state.committing = false;
                state.errorMessage = action.error.message ?? 'Failed to commit reconciliation.';
            });
    },
});

export const { clearReconcileSession, clearReconcileLists, setCareTransitionsError } = slice.actions;

const root = (state: RootState) => (state as any).careTransitions as CareTransitionsState | undefined;

export const selectReceived = (state: RootState) => root(state)?.received ?? [];
export const selectReceivedLoading = (state: RootState) => root(state)?.receivedLoading ?? false;
export const selectAudit = (state: RootState) => root(state)?.audit ?? [];
export const selectAuditLoading = (state: RootState) => root(state)?.auditLoading ?? false;
export const selectAuditPage = (state: RootState) => root(state)?.auditPage ?? [];
export const selectAuditPageLoading = (state: RootState) => root(state)?.auditPageLoading ?? false;
export const selectAuditPagePagination = (state: RootState) => root(state)?.auditPagePagination ?? { current: 1, pageSize: 10, total: 0 };
export const selectTransmissionAudit = (state: RootState) => root(state)?.transmissionAudit ?? [];
export const selectTransmissionAuditLoading = (state: RootState) => root(state)?.transmissionAuditLoading ?? false;
export const selectTransmissionAuditPage = (state: RootState) => root(state)?.transmissionAuditPage ?? [];
export const selectTransmissionAuditPageLoading = (state: RootState) => root(state)?.transmissionAuditPageLoading ?? false;
export const selectTransmissionAuditPagePagination = (state: RootState) => root(state)?.transmissionAuditPagePagination ?? { current: 1, pageSize: 10, total: 0 };
export const selectTransmittingDocumentId = (state: RootState) => root(state)?.transmittingDocumentId ?? null;
export const selectFetchRuns = (state: RootState) => root(state)?.fetchRuns ?? [];
export const selectFetchRunsLoading = (state: RootState) => root(state)?.fetchRunsLoading ?? false;
export const selectFetchRunsPagination = (state: RootState) => root(state)?.fetchRunsPagination ?? { current: 1, pageSize: 10, total: 0 };
export const selectFetchRunsFilter = (state: RootState) => root(state)?.fetchRunsFilter ?? {};
export const selectFetchRunMessages = (runId: number) => (state: RootState) => root(state)?.fetchRunDetails?.[runId] ?? null;
export const selectFetchRunDetailLoadingId = (state: RootState) => root(state)?.fetchRunDetailLoadingId ?? null;
export const selectCcdaFetching = (state: RootState) => root(state)?.fetching ?? false;
export const selectLastFetchRun = (state: RootState) => root(state)?.lastFetchRun ?? null;
export const selectInbox = (state: RootState) => root(state)?.inbox ?? [];
export const selectInboxLoading = (state: RootState) => root(state)?.inboxLoading ?? false;
export const selectInboxPagination = (state: RootState) => root(state)?.inboxPagination ?? { current: 1, pageSize: 10, total: 0 };
export const selectInboxFilter = (state: RootState) => root(state)?.inboxFilter ?? {};
export const selectReconcileSession = (state: RootState) => root(state)?.session ?? null;
export const selectReconcileSessionLoading = (state: RootState) => root(state)?.sessionLoading ?? false;
export const selectReconcileList = (listType: ReconcileListType) => (state: RootState) => root(state)?.lists?.[listType] ?? null;
export const selectReconcileListLoading = (listType: ReconcileListType) => (state: RootState) => root(state)?.listLoading?.[listType] ?? false;
export const selectReconcileSavingItemId = (state: RootState) => root(state)?.savingItemId ?? null;
export const selectReconcileConfirming = (state: RootState) => root(state)?.confirmingListType ?? null;
export const selectReconcileCommitting = (state: RootState) => root(state)?.committing ?? false;
export const selectCareTransitionsError = (state: RootState) => root(state)?.errorMessage ?? null;

export default slice.reducer;
